/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Quiz UI Renderer
 * ============================================================
 *
 * Requires:
 * 1. rise-skin-products.js
 * 2. rise-skin-quiz.js
 *
 * This file:
 * - Renders quiz questions
 * - Handles selections
 * - Handles ranked selections
 * - Handles Back / Continue
 * - Recalculates conditional branching
 * - Shows progress
 *
 * It does NOT:
 * - Define products
 * - Define quiz questions
 * - Generate recommendations
 * ============================================================
 */

window.RISE_SKIN = window.RISE_SKIN || {};


// ============================================================
// UI STATE
// ============================================================

window.RISE_SKIN.UI = {
  root: null,
  currentQuestionId: null,
  history: []
};


// ============================================================
// INITIALIZE QUIZ UI
// ============================================================

window.RISE_SKIN.initQuizUI = function (rootSelector) {

  const root = document.querySelector(rootSelector);

  if (!root) {
    console.error(
      "RISE SKIN: Quiz container not found:",
      rootSelector
    );
    return;
  }

  if (
    !Array.isArray(window.RISE_SKIN.QUIZ_QUESTIONS) ||
    !window.RISE_SKIN.QUIZ_QUESTIONS.length
  ) {
    console.error(
      "RISE SKIN: Quiz questions are not loaded."
    );
    return;
  }

  window.RISE_SKIN.UI.root = root;

  const visible =
    window.RISE_SKIN.getVisibleQuizQuestions();

  if (!visible.length) {
    console.error(
      "RISE SKIN: No visible quiz questions."
    );
    return;
  }

  window.RISE_SKIN.UI.currentQuestionId =
    visible[0].id;

  window.RISE_SKIN.UI.history = [];

  window.RISE_SKIN.renderCurrentQuestion();
};

// ============================================================
// SCROLL TO CURRENT QUIZ QUESTION
// ============================================================

window.RISE_SKIN.scrollToCurrentQuestion =
  function () {

    const shell =
      document.querySelector(
        ".rise-quiz-shell"
      );

    if (!shell) {
      return;
    }

    const top =
      shell.getBoundingClientRect().top +
      window.scrollY -
      24;

    window.scrollTo({
      top,
      behavior: "smooth"
    });
  };
// ============================================================
// CURRENT QUESTION
// ============================================================

window.RISE_SKIN.getCurrentQuestion = function () {

  return window.RISE_SKIN.getQuestionById(
    window.RISE_SKIN.UI.currentQuestionId
  );
};


// ============================================================
// VISIBLE QUESTIONS
// ============================================================

window.RISE_SKIN.getCurrentVisibleQuestions =
  function () {

    return window.RISE_SKIN
      .getVisibleQuizQuestions();
  };


// ============================================================
// SELECTED VALUES
// ============================================================

window.RISE_SKIN.getSelectedValuesForQuestion =
  function (question) {

    const answer =
      window.RISE_SKIN.quizState.answers[
        question.id
      ];

    if (
      question.type === "multi-select" ||
      question.type === "ranked-multi-select"
    ) {
      return Array.isArray(answer)
        ? answer
        : [];
    }

    if (
      answer === undefined ||
      answer === null ||
      answer === ""
    ) {
      return [];
    }

    return [answer];
  };


// ============================================================
// EXCLUSIVE OPTIONS
// ============================================================

window.RISE_SKIN.isExclusiveOption =
  function (optionValue) {

    return [
      "none",
      "unsure",
      "no_preference"
    ].includes(optionValue);
  };


// ============================================================
// SELECT / DESELECT OPTION
// ============================================================

window.RISE_SKIN.toggleQuizOption =
  function (question, optionValue) {

    // --------------------------------------------------------
    // SINGLE SELECT
    // --------------------------------------------------------

    if (question.type === "single-select") {

      window.RISE_SKIN.setQuizAnswer(
        question.id,
        optionValue
      );

      window.RISE_SKIN.renderCurrentQuestion();

      return;
    }


    // --------------------------------------------------------
    // MULTI SELECT
    // --------------------------------------------------------

    const current =
      window.RISE_SKIN
        .getSelectedValuesForQuestion(
          question
        );

    let next = [...current];


    // Exclusive options like "None"
    if (
      window.RISE_SKIN.isExclusiveOption(
        optionValue
      )
    ) {

      next = [optionValue];

    } else {

      // Remove exclusive options if a real answer is chosen.
      next = next.filter(
        (value) =>
          !window.RISE_SKIN
            .isExclusiveOption(value)
      );


      if (next.includes(optionValue)) {

        next = next.filter(
          (value) =>
            value !== optionValue
        );

      } else {

        const maxSelections =
          question.maxSelections ||
          (
            question.type ===
            "ranked-multi-select"
              ? window.RISE_SKIN
                  .quizConfig
                  .maxPrimaryConcerns
              : null
          );

        if (
          maxSelections &&
          next.length >= maxSelections
        ) {

          if (window.RISE_DEBUG) {
            console.log(
              `RISE SKIN: Maximum ${maxSelections} selections allowed`
            );
          }

          return;
        }

        next.push(optionValue);
      }
    }


    window.RISE_SKIN.setQuizAnswer(
      question.id,
      next
    );

    window.RISE_SKIN.renderCurrentQuestion();
  };


// ============================================================
// CAN CONTINUE?
// ============================================================

window.RISE_SKIN.canContinueFromQuestion =
  function (question) {

    const answer =
      window.RISE_SKIN.quizState.answers[
        question.id
      ];

    if (
      question.type === "single-select"
    ) {

      return (
        answer !== undefined &&
        answer !== null &&
        answer !== ""
      );
    }


    if (
      question.type === "multi-select" ||
      question.type ===
        "ranked-multi-select"
    ) {

      return (
        Array.isArray(answer) &&
        answer.length > 0
      );
    }

    return false;
  };


// ============================================================
// FIND NEXT VISIBLE QUESTION
// ============================================================

window.RISE_SKIN.getNextVisibleQuestion =
  function (currentQuestionId) {

    const allQuestions =
      window.RISE_SKIN.QUIZ_QUESTIONS;

    const currentIndex =
      allQuestions.findIndex(
        (question) =>
          question.id === currentQuestionId
      );

    for (
      let i = currentIndex + 1;
      i < allQuestions.length;
      i++
    ) {

      const candidate =
        allQuestions[i];

      if (
        window.RISE_SKIN
          .isQuestionVisible(candidate)
      ) {
        return candidate;
      }
    }

    return null;
  };


// ============================================================
// NEXT
// ============================================================

window.RISE_SKIN.nextQuizQuestion =
  function () {

    const current =
      window.RISE_SKIN
        .getCurrentQuestion();

    if (!current) {
      return;
    }


    if (
      !window.RISE_SKIN
        .canContinueFromQuestion(current)
    ) {

      return;
    }


    const next =
      window.RISE_SKIN
        .getNextVisibleQuestion(
          current.id
        );


    // --------------------------------------------------------
    // QUIZ COMPLETE
    // --------------------------------------------------------

    if (!next) {

      const profile =
        window.RISE_SKIN.completeQuiz();


      if (
        typeof window.RISE_SKIN
          .onQuizComplete === "function"
      ) {

        window.RISE_SKIN
          .onQuizComplete(profile);

      } else {

        window.RISE_SKIN
          .renderQuizComplete(profile);
      }

      return;
    }


    // --------------------------------------------------------
    // STORE HISTORY
    // --------------------------------------------------------

    window.RISE_SKIN.UI.history.push(
      current.id
    );

    window.RISE_SKIN.UI
  .currentQuestionId =
  next.id;

window.RISE_SKIN
  .renderCurrentQuestion();

window.requestAnimationFrame(
  function () {

    window.RISE_SKIN
      .scrollToCurrentQuestion();

  }
);
};

// ============================================================
// BACK
// ============================================================

window.RISE_SKIN.previousQuizQuestion =
  function () {

    if (
      !window.RISE_SKIN.UI
        .history.length
    ) {
      return;
    }

    const previousId =
      window.RISE_SKIN.UI
        .history.pop();

    window.RISE_SKIN.UI
      .currentQuestionId =
      previousId;

    window.RISE_SKIN
      .renderCurrentQuestion();

      window.requestAnimationFrame(
  function () {

    window.RISE_SKIN
      .scrollToCurrentQuestion();

  }
);
  };


// ============================================================
// PROGRESS
// ============================================================

window.RISE_SKIN.getQuizProgress =
  function () {

    const visible =
      window.RISE_SKIN
        .getCurrentVisibleQuestions();

    const currentId =
      window.RISE_SKIN.UI
        .currentQuestionId;

    const index =
      visible.findIndex(
        (question) =>
          question.id === currentId
      );

    const current =
      index >= 0
        ? index + 1
        : 1;

    const total =
      visible.length || 1;

    return {
      current,
      total,
      percent:
        Math.round(
          (current / total) * 100
        )
    };
  };


// ============================================================
// RANK LABEL
// ============================================================

window.RISE_SKIN.getRankLabel =
  function (rank) {

    if (rank === 1) {
      return "Top priority";
    }

    if (rank === 2) {
      return "Second";
    }

    if (rank === 3) {
      return "Third";
    }

    return `${rank}`;
  };


// ============================================================
// RENDER OPTION
// ============================================================

window.RISE_SKIN.renderQuizOption =
  function (
    question,
    option
  ) {

    const selected =
      window.RISE_SKIN
        .getSelectedValuesForQuestion(
          question
        );

    const optionValue =
      option.value || option.id;

    const selectedIndex =
      selected.indexOf(
        optionValue
      );

    const isSelected =
      selectedIndex !== -1;

    let rankMarkup = "";


    if (
      question.type ===
        "ranked-multi-select" &&
      isSelected
    ) {

      const rank =
        selectedIndex + 1;

      rankMarkup = `
        <span
          class="rise-quiz-rank"
          aria-hidden="true"
        >
          ${rank}
        </span>

        <span
          class="rise-quiz-rank-label"
        >
          ${window.RISE_SKIN
            .getRankLabel(rank)}
        </span>
      `;
    }


    return `
      <button
        type="button"
        class="
          rise-quiz-option
          ${
            isSelected
              ? "is-selected"
              : ""
          }
        "
        data-rise-option="${optionValue}"
        aria-pressed="${
          isSelected
            ? "true"
            : "false"
        }"
      >

        <span
          class="rise-quiz-option-main"
        >

          ${rankMarkup}

          <span
            class="rise-quiz-option-label"
          >
            ${option.label}
          </span>

        </span>

        <span
          class="rise-quiz-option-check"
          aria-hidden="true"
        >
          ${
            isSelected
              ? "✓"
              : ""
          }
        </span>

      </button>
    `;
  };


// ============================================================
// RENDER CURRENT QUESTION
// ============================================================

window.RISE_SKIN.renderCurrentQuestion =
  function () {

    const root =
      window.RISE_SKIN.UI.root;

    if (!root) {
      return;
    }


    const question =
      window.RISE_SKIN
        .getCurrentQuestion();

    if (!question) {
      return;
    }


    const progress =
      window.RISE_SKIN
        .getQuizProgress();


    const selected =
      window.RISE_SKIN
        .getSelectedValuesForQuestion(
          question
        );


    const canContinue =
      window.RISE_SKIN
        .canContinueFromQuestion(
          question
        );


    const optionsMarkup =
      (question.options || [])
        .map(
          (option) =>
            window.RISE_SKIN
              .renderQuizOption(
                question,
                option
              )
        )
        .join("");


    let selectionStatus = "";


    if (
      question.type ===
        "ranked-multi-select"
    ) {

      const max =
        question.maxSelections ||
        window.RISE_SKIN
          .quizConfig
          .maxPrimaryConcerns;

      selectionStatus = `
        <div
          class="rise-quiz-selection-status"
        >
          ${selected.length} of ${max}
          selected
        </div>
      `;
    }


    root.innerHTML = `

      <div class="rise-quiz-shell">


        <!-- PROGRESS -->

        <div
          class="rise-quiz-progress-wrap"
        >

          <div
            class="rise-quiz-progress-meta"
          >

            <span>
              ${progress.current}
              of
              ${progress.total}
            </span>

            <span>
              ${progress.percent}%
            </span>

          </div>


          <div
            class="rise-quiz-progress-track"
          >

            <div
              class="rise-quiz-progress-bar"
              style="
                width:
                ${progress.percent}%;
              "
            ></div>

          </div>

        </div>


        <!-- QUESTION CARD -->

        <div class="rise-quiz-card">


          ${
            question.eyebrow
              ? `
                <div
                  class="rise-quiz-eyebrow"
                >
                  ${question.eyebrow}
                </div>
              `
              : ""
          }


          <h2
            class="rise-quiz-question"
          >
            ${question.question}
          </h2>


          ${
            question.helper
              ? `
                <p
                  class="rise-quiz-helper"
                >
                  ${question.helper}
                </p>
              `
              : ""
          }


          ${selectionStatus}


          <div
            class="rise-quiz-options"
          >
            ${optionsMarkup}
          </div>


          <!-- ACTIONS -->

          <div
            class="rise-quiz-actions"
          >

            <button
              type="button"
              class="rise-quiz-back"
              ${
                window.RISE_SKIN.UI
                  .history.length
                  ? ""
                  : "disabled"
              }
            >
              Back
            </button>


            <button
              type="button"
              class="rise-quiz-next"
              ${
                canContinue
                  ? ""
                  : "disabled"
              }
            >
              Continue
            </button>

          </div>

        </div>

      </div>
    `;


    // ========================================================
    // OPTION CLICKS
    // ========================================================

    root
      .querySelectorAll(
        "[data-rise-option]"
      )
      .forEach(
        (button) => {

          button.addEventListener(
            "click",
            function () {

              window.RISE_SKIN
                .toggleQuizOption(
                  question,
                  button.dataset
                    .riseOption
                );
            }
          );
        }
      );


    // ========================================================
    // NEXT
    // ========================================================

    const nextButton =
      root.querySelector(
        ".rise-quiz-next"
      );

    if (nextButton) {

      nextButton.addEventListener(
        "click",
        window.RISE_SKIN
          .nextQuizQuestion
      );
    }


    // ========================================================
    // BACK
    // ========================================================

    const backButton =
      root.querySelector(
        ".rise-quiz-back"
      );

    if (backButton) {

      backButton.addEventListener(
        "click",
        window.RISE_SKIN
          .previousQuizQuestion
      );
    }


    if (window.RISE_DEBUG) {

      console.log(
        "RISE SKIN — rendering:",
        question.id
      );
    }
  };


// ============================================================
// TEMPORARY COMPLETION SCREEN
// ============================================================

window.RISE_SKIN.renderQuizComplete =
  function (profile) {

    const root =
      window.RISE_SKIN.UI.root;

    if (!root) {
      return;
    }


    root.innerHTML = `

      <div class="rise-quiz-shell">

        <div
          class="
            rise-quiz-card
            rise-quiz-complete
          "
        >

          <div
            class="rise-quiz-eyebrow"
          >
            Your answers are in
          </div>


          <h2
            class="rise-quiz-question"
          >
            Building your
            Rise Skin Plan…
          </h2>


          <p
            class="rise-quiz-helper"
          >
            Your quiz is working.
            Next we'll connect your
            answers to the Rise
            recommendation engine.
          </p>

        </div>

      </div>
    `;


    if (window.RISE_DEBUG) {

      console.group(
        "RISE SKIN — COMPLETED PROFILE"
      );

      console.log(profile);

      console.groupEnd();
    }
  };


// ============================================================
// UI VALIDATION
// ============================================================

window.RISE_SKIN.validateQuizUI =
  function () {

    const requiredFunctions = [
      "getVisibleQuizQuestions",
      "getQuestionById",
      "setQuizAnswer",
      "completeQuiz"
    ];

    const missing =
      requiredFunctions.filter(
        (functionName) =>
          typeof window.RISE_SKIN[
            functionName
          ] !== "function"
      );


    const report = {
      valid: missing.length === 0,
      missingFunctions: missing
    };


    if (window.RISE_DEBUG) {

      console.group(
        "RISE SKIN CONCIERGE — UI VALIDATION"
      );

      if (report.valid) {

        console.log(
          "Quiz UI dependencies found."
        );

      } else {

        console.error(
          "Missing dependencies:",
          missing
        );
      }

      console.groupEnd();
    }


    return report;
  };


// ============================================================
// INITIAL DEBUG CHECK
// ============================================================

if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Quiz UI loaded"
  );

  window.RISE_SKIN
    .validateQuizUI();
}